import React, { useState, useEffect } from 'react';
import {
  ReservationProvider,
  useReservations,
  DashboardView,
  ListView,
  CalendarView,
  TablesView,
  ZonesView,
  SettingsView,
  FloorMapView,
  ReservationModal,
  WalkInModal,
  TableModal,
  ZoneModal,
  ArrivalModal,
  WaitlistModal,
} from '../components/reservations';
import './Reservations.css';

const ReservationsContent = () => {
  const {
    loading,
    error,
    successMessage,
    fetchReservations,
    fetchDashboardData,
    fetchTables,
    fetchZones,
    fetchSettings,
    selectedDate,
    statusFilter,
  } = useReservations();

  // Tab state - Dashboard is the default for hostess workflow
  const [activeTab, setActiveTab] = useState('dashboard');

  // Modal states
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [reservationModalMode, setReservationModalMode] = useState('create');
  const [selectedReservation, setSelectedReservation] = useState(null);

  const [showWalkInModal, setShowWalkInModal] = useState(false);

  const [showTableModal, setShowTableModal] = useState(false);
  const [tableModalMode, setTableModalMode] = useState('create');
  const [selectedTable, setSelectedTable] = useState(null);

  const [showZoneModal, setShowZoneModal] = useState(false);
  const [zoneModalMode, setZoneModalMode] = useState('create');
  const [selectedZone, setSelectedZone] = useState(null);

  const [showArrivalModal, setShowArrivalModal] = useState(false);
  const [arrivalReservation, setArrivalReservation] = useState(null);

  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  // Fetch data based on active tab
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboardData();
      fetchTables();
      fetchZones();
      const interval = setInterval(fetchDashboardData, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'list' || activeTab === 'calendar') {
      fetchReservations();
    }
  }, [selectedDate, statusFilter, activeTab]);

  useEffect(() => {
    if (activeTab === 'tables') {
      fetchTables();
      fetchZones();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'settings') {
      fetchSettings();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'zones') {
      fetchZones();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'floormap') {
      fetchZones();
      fetchTables();
    }
  }, [activeTab]);

  // Modal handlers
  const handleOpenCreateReservation = () => {
    setReservationModalMode('create');
    setSelectedReservation(null);
    setShowReservationModal(true);
  };

  const handleOpenEditReservation = (reservation) => {
    setReservationModalMode('edit');
    setSelectedReservation(reservation);
    setShowReservationModal(true);
  };

  const handleOpenCreateTable = () => {
    setTableModalMode('create');
    setSelectedTable(null);
    setShowTableModal(true);
  };

  const handleOpenEditTable = (table) => {
    setTableModalMode('edit');
    setSelectedTable(table);
    setShowTableModal(true);
  };

  const handleOpenCreateZone = () => {
    setZoneModalMode('create');
    setSelectedZone(null);
    setShowZoneModal(true);
  };

  const handleOpenEditZone = (zone) => {
    setZoneModalMode('edit');
    setSelectedZone(zone);
    setShowZoneModal(true);
  };

  const handleArrival = (reservation) => {
    setArrivalReservation(reservation);
    setShowArrivalModal(true);
  };

  return (
    <div className="reservations-container">
      <div className="page-header">
        <h1>Reservations</h1>
        <p className="page-description">Manage reservations, tables, and settings</p>
      </div>

      {/* Alerts */}
      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {/* Tab Navigation */}
      <div className="tabs">
        <button
          className={`tab tab-primary ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          List
        </button>
        <button
          className={`tab ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Calendar
        </button>
        <button
          className={`tab ${activeTab === 'tables' ? 'active' : ''}`}
          onClick={() => setActiveTab('tables')}
        >
          Tables
        </button>
        <button
          className={`tab ${activeTab === 'zones' ? 'active' : ''}`}
          onClick={() => setActiveTab('zones')}
        >
          Zones
        </button>
        <button
          className={`tab ${activeTab === 'floormap' ? 'active' : ''}`}
          onClick={() => setActiveTab('floormap')}
        >
          Floor Map
        </button>
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className={`tab-content ${activeTab === 'dashboard' ? 'tab-content-dashboard' : ''}`}>
        {activeTab === 'dashboard' && (
          <DashboardView
            onArrival={handleArrival}
            onShowWaitlist={() => setShowWaitlistModal(true)}
          />
        )}
        {activeTab === 'list' && (
          <ListView
            onOpenCreate={handleOpenCreateReservation}
            onOpenWalkIn={() => setShowWalkInModal(true)}
            onEdit={handleOpenEditReservation}
          />
        )}
        {activeTab === 'calendar' && (
          <CalendarView
            onOpenCreate={handleOpenCreateReservation}
            onOpenWalkIn={() => setShowWalkInModal(true)}
            onEdit={handleOpenEditReservation}
          />
        )}
        {activeTab === 'tables' && (
          <TablesView
            onOpenCreate={handleOpenCreateTable}
            onOpenEdit={handleOpenEditTable}
          />
        )}
        {activeTab === 'zones' && (
          <ZonesView
            onOpenCreate={handleOpenCreateZone}
            onOpenEdit={handleOpenEditZone}
          />
        )}
        {activeTab === 'floormap' && <FloorMapView />}
        {activeTab === 'settings' && <SettingsView />}
      </div>

      {/* Modals */}
      <ReservationModal
        isOpen={showReservationModal}
        mode={reservationModalMode}
        reservation={selectedReservation}
        onClose={() => setShowReservationModal(false)}
      />

      <WalkInModal
        isOpen={showWalkInModal}
        onClose={() => setShowWalkInModal(false)}
      />

      <TableModal
        isOpen={showTableModal}
        mode={tableModalMode}
        table={selectedTable}
        onClose={() => setShowTableModal(false)}
      />

      <ZoneModal
        isOpen={showZoneModal}
        mode={zoneModalMode}
        zone={selectedZone}
        onClose={() => setShowZoneModal(false)}
      />

      <ArrivalModal
        isOpen={showArrivalModal}
        reservation={arrivalReservation}
        onClose={() => {
          setShowArrivalModal(false);
          setArrivalReservation(null);
        }}
      />

      <WaitlistModal
        isOpen={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
      />
    </div>
  );
};

const Reservations = () => {
  return (
    <ReservationProvider>
      <ReservationsContent />
    </ReservationProvider>
  );
};

export default Reservations;
